<template>
  <div class="projects-table">
    <!-- Filtros e ações -->
    <v-row class="mb-4">
      <v-col cols="12" md="6">
        <v-select
          v-model="projectStore.statusFilter"
          :items="statusOptions"
          item-title="text"
          item-value="value"
          :label="$t('projects.filterByStatus')"
          clearable
          outlined
          dense
          density="compact"
          @update:modelValue="onStatusFilterChange"
        >
          <template v-slot:item="{ props, item }">
            <v-list-item v-bind="props">
              <template v-slot:prepend v-if="item.raw.icon">
                <span class="mr-2">{{ item.raw.icon }}</span>
              </template>
            </v-list-item>
          </template>
        </v-select>
      </v-col>
      
      <v-col cols="12" md="6">
        <v-text-field
          v-model="searchText"
          :label="$t('projects.search')"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          density="compact"
          clearable
        ></v-text-field>
      </v-col>
    </v-row>

    <!-- Tabela de projetos -->
    <v-card>
      <v-data-table
      density="compact"
        :headers="headers"
        :items="filteredProjects"
        :loading="projectStore.loading"
        :search="searchText"
        :items-per-page="projectStore.pageSize"
        :server-items-length="projectStore.totalProjects"
        item-key="idProject"
        class="elevation-1"
        :sort-by="[{ key: 'reminderDate', order: 'asc' }]"
        @update:options="onTableOptionsUpdate"
        @click:row="viewProject"
        hover
      >
        <!-- Loading state -->
        <template v-slot:loading>
          <v-skeleton-loader type="table-row@10"></v-skeleton-loader>
        </template>

        <!-- No data state -->
        <template v-slot:no-data>
          <div class="text-center pa-4">
            <v-icon size="48" color="grey">mdi-folder-open-outline</v-icon>
            <p class="mt-2 text-grey">{{ $t('projects.noProjects') }}</p>
          </div>
        </template>

        <!-- Row styling for overdue items -->
        <template v-slot:item="{ item }">
          <tr 
            :class="{ 'warning-row': isWarning(item), 'overdue-row': !isWarning(item) && isOverdue(item) }"
            @click="viewProject(null, { item })"
            style="cursor: pointer"
          >
            <td>{{ item.idProject }}</td>
            <td>{{ item.reference }}</td>
            <td>{{ getProjectTypeDescription(item.projectType) }}</td>
            <td>{{ item.client }}</td>
            <td>{{ formatServices(item.servicesProvided) }}</td>
            <td>{{ getStatusDescription(item.activeStatus, item.projectType) }}</td>
            <td>{{ calculateDaysDelayed(item.statusChangeDate) }}</td>
            <td :class="{ 'text-orange font-weight-bold': isWarning(item), 'text-red font-weight-bold': !isWarning(item) && isOverdue(item) }">
              {{ formatDate(item.reminderDate) }}
            </td>
            <td>{{ formatDate(item.estimatedDepartureDate) }}</td>
            <td>{{ formatDate(item.estimatedArriveDate) }}</td>
            <td>{{ formatDate(item.numeraryValueDate) }}</td>
            <td>
              <v-btn
                icon
                size="small"
                @click.stop="openProjectFolder(item)"
                :title="$t('projects.openFolder')"
                :disabled="!localBackendStore.isConnected"
              >
                <v-icon size="small">mdi-folder-open</v-icon>
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <!-- Actions Menu -->
    <v-menu v-model="showActionsMenu" :activator="actionsMenuActivator" offset-y>
      <v-list>
        <v-list-item @click="viewProject(selectedProjectForActions)">
          <template v-slot:prepend>
            <v-icon>mdi-eye</v-icon>
          </template>
          <v-list-item-title>Visualizar</v-list-item-title>
        </v-list-item>
        
        <v-list-item @click="editProject(selectedProjectForActions)">
          <template v-slot:prepend>
            <v-icon>mdi-pencil</v-icon>
          </template>
          <v-list-item-title>Editar</v-list-item-title>
        </v-list-item>
        
        <v-divider></v-divider>
        
        <v-list-item @click="deleteProject(selectedProjectForActions)" class="text-red">
          <template v-slot:prepend>
            <v-icon color="red">mdi-delete</v-icon>
          </template>
          <v-list-item-title>Deletar</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- Project Form Dialog -->
    <ProjectForm
      v-if="showProjectForm"
      v-model="showProjectForm"
      :project="selectedProject"
      :is-editing="isEditing"
      @project-saved="onProjectSaved"
    />

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t('projects.confirmDelete') }}</span>
        </v-card-title>
        <v-card-text>
          {{ $t('projects.deleteMessage', { reference: projectToDelete?.reference }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showDeleteDialog = false">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn color="red" text @click="confirmDelete" :loading="deleting">
            {{ $t('common.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useProjectStore } from '../stores/projects.js'
import { useLocalBackendStore } from '../stores/localBackend.js'
import ProjectForm from './ProjectForm.vue'
import { useI18n } from 'vue-i18n'
import { calculateBusinessDays } from '../utils/dateUtils.js'

const projectStore = useProjectStore()
const localBackendStore = useLocalBackendStore()
const { t } = useI18n()

// Define emits
const emit = defineEmits(['project-saved'])

// Estado local
const searchText = ref('')
const showProjectForm = ref(false)
const showDeleteDialog = ref(false)
const selectedProject = ref(null)
const projectToDelete = ref(null)
const isEditing = ref(false)
const deleting = ref(false)
const showActionsMenu = ref(false)
const actionsMenuActivator = ref(null)
const selectedProjectForActions = ref(null)

// Headers da tabela
const headers = ref([
  { title: t('projects.table.id'), key: 'idProject', width: '150px' },
  { title: t('projects.table.reference'), key: 'reference', width: '120px' },
  { 
    title: t('projects.table.type'), 
    key: 'projectType', 
    width: '100px',
    value: item => getProjectTypeDescription(item.projectType)
  },
  { title: t('projects.table.client'), key: 'client', width: '130px' },
  { 
    title: t('projects.table.services'), 
    key: 'servicesProvided', 
    width: '120px',
    value: item => formatServices(item.servicesProvided)
  },
  { 
    title: t('projects.table.status'), 
    key: 'activeStatus', 
    width: '140px',
    value: item => getStatusDescription(item.activeStatus, item.projectType)
  },
  { 
    title: t('projects.table.daysDelayed'), 
    key: 'daysDelayed', 
    width: '110px',
    value: item => calculateDaysDelayed(item.statusChangeDate)
  },
  { 
    title: t('projects.table.dueDate'), 
    key: 'reminderDate', 
    width: '110px'
  },
  { 
    title: t('projects.table.estimatedDeparture'), 
    key: 'estimatedDepartureDate', 
    width: '110px'
  },
  { 
    title: t('projects.table.estimatedArrival'), 
    key: 'estimatedArriveDate', 
    width: '110px'
  },
  { 
    title: t('projects.table.numeraryDate'), 
    key: 'numeraryValueDate', 
    width: '110px'
  },
  { 
    title: t('projects.table.actions'), 
    key: 'actions', 
    width: '80px',
    sortable: false
  }
])

// Computed
const statusOptions = computed(() => {
  const options = [
    { text: 'Todos', value: null, icon: null }
  ]
  
  projectStore.statusList.forEach(status => {
    options.push({
      text: status.descr,
      value: status.id,
      icon: status.icon
    })
  })
  
  return options
})

const filteredProjects = computed(() => {
  return projectStore.filteredProjects
})

// Métodos
const formatDate = (dateString) => {
  if (!dateString) return '-'
  
  // Extract just the date part (YYYY-MM-DD) to avoid timezone conversion issues
  const datePart = dateString.split('T')[0]
  const [year, month, day] = datePart.split('-')
  
  // Create date using local timezone (no conversion)
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
  return date.toLocaleDateString('pt-BR')
}

const calculateDaysDelayed = (statusChangeDate) => {
  if (!statusChangeDate) return '-'
  
  // Parse the date string correctly
  const datePart = statusChangeDate.split('T')[0]
  const [year, month, day] = datePart.split('-')
  const changeDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
  
  // Get today's date
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  // Calculate business days between changeDate and today
  const businessDays = calculateBusinessDays(changeDate, today)
  
  return businessDays
}

const isWarning = (project) => {
  if (!project.reminderDate) return false
  
  // Get today's date in local timezone
  const today = new Date()
  const todayStr = today.getFullYear() + '-' + 
                   String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                   String(today.getDate()).padStart(2, '0')
  
  // Extract date part from reminderDate (ignore time and timezone)
  const reminderStr = project.reminderDate.substring(0, 10)
  
  // Check if reminder date is exactly today
  return reminderStr === todayStr
}

const isOverdue = (project) => {
  if (!project.reminderDate) return false
  
  // Get today's date in local timezone
  const today = new Date()
  const todayStr = today.getFullYear() + '-' + 
                   String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                   String(today.getDate()).padStart(2, '0')
  
  // Extract date part from reminderDate (ignore time and timezone)
  const reminderStr = project.reminderDate.substring(0, 10)
  
  // Check if reminder date is BEFORE today (yesterday or earlier)
  return reminderStr < todayStr
}

const getStatusInfo = (statusId, projectTypeId = null) => {
  return projectStore.getStatusInfo(statusId, projectTypeId)
}

const getStatusDescription = (statusId, projectTypeId = null) => {
  const status = getStatusInfo(statusId, projectTypeId)
  return status?.descr || 'Desconhecido'
}

const getStatusIcon = (statusId, projectTypeId = null) => {
  const status = getStatusInfo(statusId, projectTypeId)
  return status?.icon || '❓'
}

const getStatusColor = (statusId, projectTypeId = null) => {
  const status = getStatusInfo(statusId, projectTypeId)
  return status?.color || '#grey'
}

const getProjectTypeDescription = (typeId) => {
  if (typeId === null || typeId === undefined) return '-'
  const type = projectStore.getProjectTypeInfo(typeId)
  return type?.descr || 'Desconhecido'
}

const formatServices = (services) => {
  if (!services || !Array.isArray(services) || services.length === 0) return '-'
  // Convert array to uppercase string, separated by commas
  return services.map(s => s.toUpperCase()).join(', ')
}

const onStatusFilterChange = (status) => {
  projectStore.setStatusFilter(status)
}

const onTableOptionsUpdate = (options) => {
  // Implementar paginação se necessário
  if (options.page !== projectStore.currentPage) {
    projectStore.loadProjects(options.page, projectStore.statusFilter)
  }
}

const openCreateDialog = () => {
  selectedProject.value = null
  isEditing.value = false
  showProjectForm.value = true
}

const viewProject = (event, row) => {
  const project = row?.item || event
  selectedProject.value = project
  isEditing.value = true
  showProjectForm.value = true
}

const editProject = (project) => {
  selectedProject.value = project
  isEditing.value = true
  showProjectForm.value = true
}

const deleteProject = (project) => {
  projectToDelete.value = project
  showDeleteDialog.value = true
}

const confirmDelete = async () => {
  if (!projectToDelete.value) return
  
  try {
    deleting.value = true
    await projectStore.deleteProject(projectToDelete.value.idProject)
    showDeleteDialog.value = false
    projectToDelete.value = null
  } catch (error) {
    console.error('Erro ao deletar projeto:', error)
  } finally {
    deleting.value = false
  }
}

const onProjectSaved = () => {
  // Recarregar lista de projetos
  projectStore.loadProjects(projectStore.currentPage, projectStore.statusFilter)
  // Emitir evento para o componente pai
  emit('project-saved')
}

const openProjectFolder = async (project) => {
  if (!project?.idProject) return
  
  try {
    await projectStore.openProjectFolder(project.idProject)
    console.log('Folder opened for project:', project.idProject)
  } catch (error) {
    console.error('Error opening project folder:', error)
    // Opcionalmente potresti mostrare un messaggio di errore all'utente
  }
}

// Lifecycle
onMounted(async () => {
  // Carregar configurações e projetos
  await projectStore.loadConfigurations()
  await projectStore.loadProjects()
})

// Expose methods to parent components
defineExpose({
  openCreateDialog
})
</script>

<style scoped>
.projects-table {
  width: 100%;
}

.warning-row {
  background-color: #fff9c4 !important;
}

.warning-row:hover {
  background-color: #fff59d !important;
}

.overdue-row {
  background-color: #ffebee !important;
}

.overdue-row:hover {
  background-color: #ffcdd2 !important;
}
</style>